import { useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "../../../shared/lib";
import {
  searchCustomers,
  SearchCustomersParams,
  SearchCustomersResult,
} from "../../../shared/api/broadcastNotifications";

type UseCustomerSearchState = SearchCustomersResult & {
  isLoading: boolean;
  error: string | null;
};

const customerSearchCache = new Map<string, SearchCustomersResult>();

export const useCustomerSearch = (params: SearchCustomersParams) => {
  const [state, setState] = useState<UseCustomerSearchState>({
    items: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const requestKey = JSON.stringify(params);
  const requestParams = useMemo(() => params, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = customerSearchCache.get(requestKey);
      setState((prev) => ({
        items: cached?.items ?? prev.items,
        meta: cached?.meta ?? prev.meta,
        isLoading: !cached,
        error: null,
      }));

      try {
        const result = await searchCustomers(requestParams);
        if (!isMounted) return;
        customerSearchCache.set(requestKey, result);
        setState({ ...result, isLoading: false, error: null });
      } catch (error) {
        if (!isMounted) return;
        setState({
          items: [],
          meta: undefined,
          isLoading: false,
          error: getApiErrorMessage(error, "Unable to search customers"),
        });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [requestKey, requestParams]);

  return state;
};
